// 桌面布局 store。对应 docs/specs/260508-folder-feature/plan.md §3.2 + design-codex.md §5.1。
//
// 形态:
//   ┌────────────┐ load()           ┌──────────────┐
//   │ status:    │ ────────────────►│ /api/v1/     │
//   │ idle |     │                  │ desktop/     │
//   │ loading |  │ ◄────────────────│ layout       │
//   │ ready |    │                  └──────────────┘
//   │ error      │
//   └─────┬──────┘
//         │ applyLocalChange(mutator) — 乐观更新 + debounced flush
//         ▼
//   pending=true ─► PUT ─► success: pending=false / lastSyncError=null
//                   │
//                   ├─ network/5xx: 退避重试 5 次,全失败 pending 保持 true,lastSyncError='network'
//                   ├─ 401:        会话过期 → session 置 anonymous 交路由跳登录,不弹通知
//                   ├─ 409:        load() 拉服务端 + 'warning' 通知,本地未保存被丢弃
//                   └─ 4xx 校验:    load() + 'danger' 通知
//
// 注意:flush() 的"无 pending 立即 resolve / 有 pending 立即 PUT / 失败抛错给
// caller"三段契约由 M3 确定;M6 App Installer 联动直接复用,不允许在 M6 改契约。

import type { AxiosError } from 'axios';
import { create } from 'zustand';

import type {
  DesktopFolderDTO,
  DesktopItemDTO,
  DesktopLayoutDTO,
} from '@blade/os-sdk';
import { http } from '@blade/os-sdk';

import { pushNotification } from './notificationStore';
import { useSessionStore } from './sessionStore';

const ENDPOINT = '/desktop/layout';
const RESET_ENDPOINT = '/desktop/layout/reset';

// 退避间隔(ms)。共 5 次 attempt,因此只有 4 次等待。
const RETRY_BACKOFF_MS = [500, 1000, 2000, 4000] as const;
const DEBOUNCE_MS = 500;

// 同步失败通知的 dedupe key,避免重试期间刷屏(notificationStore 自带 500ms 去重窗)。
const SYNC_NOTICE_KEY = 'desktop-layout-sync';

export type DesktopLayoutStatus = 'idle' | 'loading' | 'ready' | 'error';

/**
 * lastSyncError:仅供调试 / E2E 断言。UI 不直接读它,用户可见的失败提示统一
 * 走 ``pushNotification(...)``。取值:
 *   - null      正常或刚成功
 *   - 'load'    GET 失败
 *   - 'network' PUT 网络/5xx 重试 5 次后仍失败
 *   - 'conflict' PUT 收到 409
 *   - 'validation' PUT 收到 4xx 校验错误
 *   - 'auth'    401(会话过期);store 已把 session 置为 anonymous 交路由跳登录
 */
export type DesktopLayoutSyncError =
  | null
  | 'load'
  | 'network'
  | 'conflict'
  | 'validation'
  | 'auth';

export interface DesktopLayoutState {
  status: DesktopLayoutStatus;
  layout: DesktopLayoutDTO | null;
  /** 是否有 PUT 在飞 / 在 debounce 等待中。UI 用它决定"同步中"角标。 */
  pending: boolean;
  lastSyncError: DesktopLayoutSyncError;
  openFolderId: string | null;

  load: () => Promise<void>;
  setOpenFolder: (folderId: string | null) => void;
  applyLocalChange: (mutator: (draft: DesktopLayoutDTO) => void) => void;
  flush: () => Promise<void>;
  resetToFactory: () => Promise<void>;
}

// ---------------------------------------------------------------------------
// 模块级 debounce 单例 timer + 去抖动正在等待的 promise
// ---------------------------------------------------------------------------
//
// 不放在闭包里,因为 Vite fast-refresh 会重建闭包导致出现多个 timer。

let _debounceTimer: ReturnType<typeof setTimeout> | null = null;
let _flushInFlight: Promise<void> | null = null;

// 同一时刻只允许一次 GET 在飞。首屏至少有三处会同时喊 load():Desktop 挂载、
// ProtectedShell 的 visibilitychange/focus 重拉、App Installer 装完的联动。
// 并发 GET 在服务端会撞"同一用户两个请求都发现没有布局行 → 都 INSERT →
// 唯一索引冲突 → 500",前端就弹"加载桌面布局失败,请检查网络"。服务端已做
// 防御(desktop_layout_service._persist_initial),这里再从源头掐掉并发。
let _loadInFlight: Promise<void> | null = null;

// M6 / T-6.2.2:首次成功 load 之后从 localStorage 清掉旧 iconsStore 的坐标,
// 避免老用户调试时还看到 'blade-os:icons:v1' 残留。一个 boolean 保证只跑一次。
let _legacyIconsCleanedUp = false;
const LEGACY_ICONS_KEY = 'blade-os:icons:v1';

function _cleanupLegacyIconsOnce(): void {
  if (_legacyIconsCleanedUp) return;
  _legacyIconsCleanedUp = true;
  try {
    localStorage.removeItem(LEGACY_ICONS_KEY);
  } catch {
    // 浏览器禁用 localStorage / 测试环境没有 → 静默吞,不影响 load 成功
  }
}

// 仅供测试重置 module-level state(测试 fixture 在 beforeEach 中调用)。
export function _resetDesktopLayoutModuleStateForTests(): void {
  _legacyIconsCleanedUp = false;
  if (_debounceTimer !== null) {
    clearTimeout(_debounceTimer);
    _debounceTimer = null;
  }
  _flushInFlight = null;
  _loadInFlight = null;
}

function _clearDebounce(): void {
  if (_debounceTimer !== null) {
    clearTimeout(_debounceTimer);
    _debounceTimer = null;
  }
}

// ---------------------------------------------------------------------------
// 工具:深拷贝、错误分类
// ---------------------------------------------------------------------------

function _clone(layout: DesktopLayoutDTO): DesktopLayoutDTO {
  // 项目 frontend/package.json 没装 immer;structuredClone 在所有 evergreen
  // 浏览器 + jsdom 24+ 中可用,够用。
  return globalThis.structuredClone(layout);
}

function _sameLayout(a: DesktopLayoutDTO | null, b: DesktopLayoutDTO | null): boolean {
  if (a === null || b === null) return a === b;
  return JSON.stringify(a) === JSON.stringify(b);
}

interface ConflictDetail {
  code?: string;
  serverVersion?: number;
}

function _classifyAxiosError(err: unknown):
  | { kind: 'network' }
  | { kind: 'auth' }
  | { kind: 'conflict'; serverVersion: number | null }
  | { kind: 'validation'; detail: unknown }
  | { kind: 'other' } {
  const ax = err as AxiosError | undefined;
  if (!ax || !ax.isAxiosError) {
    return { kind: 'other' };
  }
  const status = ax.response?.status;
  if (status === undefined || status >= 500) {
    return { kind: 'network' };
  }
  if (status === 401) {
    // http.ts 的拦截器只在会话确认失效(/auth/refresh 被服务端明确拒绝,或
    // refresh 后重放仍 401)时打 bladeSessionExpired 标记。不带标记的 401
    // 是 refresh 因网络 / 5xx 瞬时失败抛回的,按网络错误处理(可重试),
    // 不能据此登出丢弃用户未保存的修改。
    if ((ax as AxiosError & { bladeSessionExpired?: boolean }).bladeSessionExpired) {
      return { kind: 'auth' };
    }
    return { kind: 'network' };
  }
  if (status === 409) {
    const detail = (ax.response?.data as { detail?: ConflictDetail } | undefined)
      ?.detail;
    return {
      kind: 'conflict',
      serverVersion:
        typeof detail?.serverVersion === 'number' ? detail.serverVersion : null,
    };
  }
  if (status >= 400 && status < 500) {
    return { kind: 'validation', detail: ax.response?.data };
  }
  return { kind: 'other' };
}

function _sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

// ---------------------------------------------------------------------------
// load 的实现体
// ---------------------------------------------------------------------------
//
// 抽成模块级函数只是为了让 ``load()`` 那层能干净地做"同一时刻只有一个 GET"
// 的去重;行为与内联时完全一致。

type SetState = (partial: Partial<DesktopLayoutState>) => void;
type GetState = () => DesktopLayoutState;

async function _load(set: SetState, get: GetState): Promise<void> {
  const hadLayout = get().layout !== null;
  set({ status: 'loading' });
  try {
    const resp = await http().get<DesktopLayoutDTO>(ENDPOINT);
    // 成功 GET 一次后认为本地与服务端一致,清掉"已自我修复"类的错误码
    // ('load' / 'network');保留 'conflict' / 'validation' 作为调试 / E2E
    // 痕迹 —— 这两类是用户操作被拒后由 flush 主动跑的 load,UI 已经发了
    // 通知,标志位留着方便事后断言"是因为 409 / 422 才回滚的"。
    const { lastSyncError } = get();
    const nextError =
      lastSyncError === 'conflict' || lastSyncError === 'validation'
        ? lastSyncError
        : null;
    set({
      status: 'ready',
      layout: resp.data,
      pending: false,
      lastSyncError: nextError,
    });
    // 旧 iconsStore 的 localStorage 坐标在新模型下不再使用;首次成功 load 后
    // 静默清理一次(M6 / T-6.2.2)。
    _cleanupLegacyIconsOnce();
  } catch (err) {
    if (_classifyAxiosError(err).kind === 'auth') {
      // 会话过期不是网络问题:弹"检查网络"只会误导。置 anonymous 让
      // RequireAuth 直接跳登录页,不弹通知。
      set({ status: 'error', lastSyncError: 'auth' });
      useSessionStore.getState().setAnonymous();
      throw err;
    }
    if (hadLayout) {
      // 后台重拉(标签页重新聚焦 / 安装联动)失败:桌面上已有可用布局,
      // 保持 ready 静默降级,不为一次瞬时失败打扰用户;lastSyncError
      // 留痕供调试 / E2E 断言。
      set({ status: 'ready', lastSyncError: 'load' });
      throw err;
    }
    set({ status: 'error', lastSyncError: 'load' });
    pushNotification({
      level: 'warning',
      message: '加载桌面布局失败,可重试或刷新页面',
      key: SYNC_NOTICE_KEY,
    });
    throw err;
  }
}

// ---------------------------------------------------------------------------
// store
// ---------------------------------------------------------------------------

export const useDesktopLayoutStore = create<DesktopLayoutState>((set, get) => ({
  status: 'idle',
  layout: null,
  pending: false,
  lastSyncError: null,
  openFolderId: null,

  setOpenFolder(folderId) {
    set({ openFolderId: folderId });
  },

  // -------------------------------------------------------------------------
  // load(): GET /desktop/layout
  // -------------------------------------------------------------------------
  async load() {
    if (_loadInFlight !== null) {
      // 已有 GET 在飞 → 共享它,不再打第二枪。成功 / 失败语义与自己发一次一致。
      return _loadInFlight;
    }
    const run = _load(set, get);
    _loadInFlight = run;
    try {
      await run;
    } finally {
      _loadInFlight = null;
    }
  },

  // -------------------------------------------------------------------------
  // applyLocalChange(): 乐观更新 + debounced flush
  // -------------------------------------------------------------------------
  applyLocalChange(mutator) {
    const current = get().layout;
    if (current === null) {
      // 没有 layout 时调用是误用 —— 不抛错,只 log 提示。
      // (load() 还没成功就改本地状态,后端 PUT 也无法做有意义的乐观锁。)
      console.warn(
        'desktopLayoutStore.applyLocalChange called before load() — ignored',
      );
      return;
    }
    const draft = _clone(current);
    mutator(draft);
    set({ layout: draft, pending: true });

    _clearDebounce();
    _debounceTimer = setTimeout(() => {
      _debounceTimer = null;
      // 不 await:UI 已经更新过,flush 在后台自己完成。
      void get().flush();
    }, DEBOUNCE_MS);
  },

  // -------------------------------------------------------------------------
  // flush(): 把 pending 状态推到服务端
  //
  // 契约(M6 App Installer 依赖,不要在 M6 改):
  //   ① 无 pending → 立即 resolve
  //   ② 有 pending → 取消 debounce,立即 PUT,await 完成
  //   ③ 成功:用响应替换 layout,pending=false / lastSyncError=null
  //   ④ 失败:网络/5xx 退避重试 5 次,全失败保持 pending=true + lastSyncError='network'
  //          409:load() + 'warning' 通知 + pending=false
  //          4xx:load() + 'danger' 通知 + pending=false
  //   ⑤ 任何最终失败都把异常抛给 caller
  // -------------------------------------------------------------------------
  async flush() {
    const state = get();
    if (!state.pending || state.layout === null) {
      return;
    }
    if (_flushInFlight !== null) {
      // 已有一次 flush 正在跑;等它完成即可。caller 之后会再看 pending 状态。
      return _flushInFlight;
    }

    _clearDebounce();

    const flush = (async () => {
      try {
        let lastErr: unknown = null;
        while (get().pending) {
          // 每轮保存一次当前快照。若 PUT 飞行期间用户继续修改,成功响应不能
          // 覆盖那份新本地状态;只接收服务端 version 后继续下一轮 PUT。
          const target = get().layout;
          if (target === null) return;

          try {
            const resp = await http().put<DesktopLayoutDTO>(ENDPOINT, target);

            const current = get().layout;
            if (_sameLayout(current, target)) {
              set({
                layout: resp.data,
                pending: false,
                lastSyncError: null,
              });
              return;
            }

            if (current !== null) {
              set({
                layout: { ...current, version: resp.data.version },
                pending: true,
                lastSyncError: null,
              });
            }
            lastErr = null;
          } catch (err) {
            const cls = _classifyAxiosError(err);

            if (cls.kind === 'auth') {
              // 会话过期:重试 / 重新 load 都只会再吃 401。置 anonymous
              // 跳登录,未保存修改随会话一起失效,不再弹网络类通知。
              set({ lastSyncError: 'auth', pending: false });
              useSessionStore.getState().setAnonymous();
              throw err;
            }

            if (cls.kind === 'conflict') {
              pushNotification({
                level: 'warning',
                message: '桌面布局已在其他设备更新,未保存的修改已丢弃',
                key: SYNC_NOTICE_KEY,
              });
              set({ lastSyncError: 'conflict', pending: false });
              try {
                await get().load();
              } catch {
                // load 内部已 push 通知,这里吞掉避免双弹。
              }
              throw err;
            }

            if (cls.kind === 'validation') {
              pushNotification({
                level: 'danger',
                message: '布局保存被服务端拒绝,已恢复到上次成功状态',
                key: SYNC_NOTICE_KEY,
              });
              set({ lastSyncError: 'validation', pending: false });
              try {
                await get().load();
              } catch {
                // ditto
              }
              throw err;
            }

            // network / other → 退避重试
            lastErr = err;
            for (let retry = 0; retry < RETRY_BACKOFF_MS.length; retry += 1) {
              const delay = RETRY_BACKOFF_MS[retry];
              if (delay === undefined) break;
              pushNotification({
                level: 'warning',
                message: '同步失败,正在重试',
                key: SYNC_NOTICE_KEY,
              });
              await _sleep(delay);
              try {
                const latest = get().layout;
                if (latest === null) return;
                const resp = await http().put<DesktopLayoutDTO>(ENDPOINT, latest);
                const current = get().layout;
                if (_sameLayout(current, latest)) {
                  set({
                    layout: resp.data,
                    pending: false,
                    lastSyncError: null,
                  });
                  return;
                }
                if (current !== null) {
                  set({
                    layout: { ...current, version: resp.data.version },
                    pending: true,
                    lastSyncError: null,
                  });
                }
                lastErr = null;
                break;
              } catch (retryErr) {
                const retryCls = _classifyAxiosError(retryErr);
                if (retryCls.kind === 'network' || retryCls.kind === 'other') {
                  lastErr = retryErr;
                  continue;
                }
                if (retryCls.kind === 'auth') {
                  set({ lastSyncError: 'auth', pending: false });
                  useSessionStore.getState().setAnonymous();
                  throw retryErr;
                }
                if (retryCls.kind === 'conflict') {
                  pushNotification({
                    level: 'warning',
                    message: '桌面布局已在其他设备更新,未保存的修改已丢弃',
                    key: SYNC_NOTICE_KEY,
                  });
                  set({ lastSyncError: 'conflict', pending: false });
                  try {
                    await get().load();
                  } catch {
                    // load 内部已 push 通知,这里吞掉避免双弹。
                  }
                  throw retryErr;
                }
                if (retryCls.kind === 'validation') {
                  pushNotification({
                    level: 'danger',
                    message: '布局保存被服务端拒绝,已恢复到上次成功状态',
                    key: SYNC_NOTICE_KEY,
                  });
                  set({ lastSyncError: 'validation', pending: false });
                  try {
                    await get().load();
                  } catch {
                    // ditto
                  }
                  throw retryErr;
                }
                throw retryErr;
              }
            }
            if (lastErr !== null) {
              break;
            }
          }
        }

        // 5 次都失败 → 保持 pending=true,等用户后续操作或刷新再触发。
        set({ lastSyncError: 'network' });
        pushNotification({
          level: 'warning',
          message: '同步失败,请检查网络;最近的修改尚未保存',
          key: SYNC_NOTICE_KEY,
        });
        throw lastErr;
      } finally {
        _flushInFlight = null;
      }
    })();

    _flushInFlight = flush;
    return flush;
  },

  // -------------------------------------------------------------------------
  // resetToFactory(): POST /desktop/layout/reset
  // -------------------------------------------------------------------------
  async resetToFactory() {
    try {
      const resp = await http().post<DesktopLayoutDTO>(RESET_ENDPOINT);
      set({
        status: 'ready',
        layout: resp.data,
        pending: false,
        lastSyncError: null,
      });
    } catch (err) {
      pushNotification({
        level: 'danger',
        message: '恢复出厂布局失败,请稍后重试',
        key: SYNC_NOTICE_KEY,
      });
      throw err;
    }
  },
}));
